import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './AffiliatePage.css';
import useLang from '../../hooks/use-lang/useLang.js';
import { listPromoCodes, createPromoCode, deletePromoCode, regeneratePromoCode, revokePromoCode } from '../../api/promocodesAPI/promocodesAPI.js';
import { listPlans } from '../../api/plansAPI/plansAPI.js';

const CODE_CHARSET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function generateCode() {
  return Array.from({ length: 8 }, () => CODE_CHARSET[Math.floor(Math.random() * CODE_CHARSET.length)]).join('');
}

const PROMO_TYPES = [
  { value: 'FIXED', labelKey: 'affiliate.typeFixed', fallback: 'Фікса', hintKey: 'affiliate.typeFixedHint', hintFallback: 'мінус $ з оплати' },
  { value: 'PERCENT', labelKey: 'affiliate.typePercent', fallback: 'Відсоток', hintKey: 'affiliate.typePercentHint', hintFallback: 'мінус % з оплати' },
  { value: 'FREE_PERIOD', labelKey: 'affiliate.typeFreePeriod', fallback: 'Безкоштовно', hintKey: 'affiliate.typeFreePeriodHint', hintFallback: 'дні оренди' },
  { value: 'BALANCE_BONUS', labelKey: 'affiliate.typeBalanceBonus', fallback: 'Бонус', hintKey: 'affiliate.typeBalanceBonusHint', hintFallback: 'при поповненні' },
  { value: 'PARTNER', labelKey: 'affiliate.typePartner', fallback: 'Партнер', hintKey: 'affiliate.typePartnerHint', hintFallback: 'знижка + винагорода' },
  { value: 'MULTI_SERVER', labelKey: 'affiliate.typeMultiServer', fallback: 'Мульти', hintKey: 'affiliate.typeMultiServerHint', hintFallback: 'від N серверів' },
];

function AffiliatePage({ onNavigate, onLogout }) {
  const { t } = useLang();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [creatorId, setCreatorId] = useState('');
  const [creatorName, setCreatorName] = useState('');
  const [promoType, setPromoType] = useState('FIXED');
  const [discountValue, setDiscountValue] = useState('');
  const [partnerReward, setPartnerReward] = useState('');
  const [clientDiscountType, setClientDiscountType] = useState('FIXED');
  const [minServers, setMinServers] = useState('');
  const [freeDays, setFreeDays] = useState('');
  const [planId, setPlanId] = useState('');
  const [plans, setPlans] = useState([]);
  const [planOpen, setPlanOpen] = useState(false);
  const planDropRef = useRef(null);
  const [maxUses, setMaxUses] = useState('');
  const [validDays, setValidDays] = useState('');
  const [promos, setPromos] = useState([]);
  const [promoFilter, setPromoFilter] = useState('all');
  const [filterPill, setFilterPill] = useState({ left: 0, width: 0, visible: false });
  const filterRefs = useRef({});
  const filtersInnerRef = useRef(null);

  const updateFilterPill = () => {
    const el = filterRefs.current[promoFilter];
    const nav = filtersInnerRef.current;
    if (!el || !nav) return;
    const elRect = el.getBoundingClientRect();
    const navRect = nav.getBoundingClientRect();
    setFilterPill({
      left: elRect.left - navRect.left,
      width: elRect.width,
      visible: true,
    });
  };

  useLayoutEffect(() => {
    updateFilterPill();
    window.addEventListener('resize', updateFilterPill);
    return () => window.removeEventListener('resize', updateFilterPill);
  }, [promoFilter]);
  const [copiedId, setCopiedId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    listPromoCodes().then(setPromos).catch(() => {});
    listPlans().then((rows) => setPlans(rows.filter((p) => p.isActive))).catch(() => {});
    const onDocClick = (e) => {
      if (planDropRef.current && !planDropRef.current.contains(e.target)) setPlanOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleGenerate = () => {
    if (isGenerating) return;
    setIsGenerating(true);
    let ticks = 0;
    const timer = setInterval(() => {
      ticks += 1;
      setCode(generateCode());
      if (ticks >= 12) {
        clearInterval(timer);
        setIsGenerating(false);
      }
    }, 55);
  };

  const handleCreate = async () => {
    const normalized = code.trim();
    if (!normalized || isCreating) return;
    setIsCreating(true);
    try {
      const promo = await createPromoCode({
        code: normalized,
        name: name.trim(),
        creatorId: creatorId.trim(),
        creatorName: creatorName.trim(),
        type: promoType,
        discountValue: discountValue.trim(),
        partnerReward: partnerReward.trim(),
        clientDiscountType,
        minServers: minServers.trim(),
        freeDays: freeDays.trim(),
        planId: planId || null,
        maxUses: maxUses.trim(),
        validDays: validDays.trim(),
      });
      setPromos((prev) => [promo, ...prev]);
      setCode('');
      setName('');
      setCreatorId('');
      setCreatorName('');
      setDiscountValue('');
      setPartnerReward('');
      setMinServers('');
      setFreeDays('');
      setMaxUses('');
      setValidDays('');
    } catch (error) {
      const messages = {
        CODE_EXISTS: t('affiliate.codeExists', 'Такий промокод уже існує'),
        USER_NOT_FOUND: t('affiliate.userNotFound', 'Користувача з таким Telegram ID не знайдено'),
      };
      alert(messages[error.message] || error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const canCreate = (() => {
    if (!code.trim() || !name.trim()) return false;
    if (promoType === 'FREE_PERIOD') return !!freeDays.trim();
    if (promoType === 'MULTI_SERVER') return !!(discountValue.trim() && minServers.trim());
    if (promoType === 'PARTNER') {
      if (!creatorId.trim() || !creatorName.trim() || !partnerReward.trim()) return false;
      return clientDiscountType === 'FREE_PERIOD' ? !!freeDays.trim() : !!discountValue.trim();
    }
    return !!discountValue.trim();
  })();

  const handleCopy = async (promo) => {
    try {
      await navigator.clipboard.writeText(promo.code);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = promo.code;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopiedId(promo.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleRegenerate = async (promo) => {
    if (!window.confirm(t('affiliate.regenerateConfirm', 'Згенерувати новий код? Статистика збережеться'))) return;
    try {
      const updated = await regeneratePromoCode(promo.id);
      setPromos((prev) => prev.map((p) => (p.id === promo.id ? updated : p)));
    } catch (error) {
      alert(error.message);
    }
  };

  const handleRevoke = async (promo) => {
    if (!window.confirm(t('affiliate.revokeConfirm', 'Забрати партнерку у цього користувача?'))) return;
    try {
      await revokePromoCode(promo.id);
      setPromos((prev) => prev.filter((p) => p.id !== promo.id));
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDelete = async (promo) => {
    if (!window.confirm(t('affiliate.deleteConfirm', 'Видалити цей промокод?'))) return;
    try {
      await deletePromoCode(promo.id);
      setPromos((prev) => prev.filter((p) => p.id !== promo.id));
    } catch (error) {
      alert(error.message);
    }
  };

  const filteredPromos = promos.filter((p) =>
    promoFilter === 'all' ? true : promoFilter === 'partner' ? p.type === 'PARTNER' : p.type !== 'PARTNER',
  );

  const stats = {
    partners: promos.filter((p) => p.type === 'PARTNER').length,
    activations: promos.reduce((sum, p) => sum + p.referrals, 0),
    paidWithCodes: promos.reduce((sum, p) => sum + p.ownEarned, 0),
    paidToPartners: promos.reduce((sum, p) => sum + p.partnerEarned, 0),
  };

  return (
    <div className="affiliate__page">
      <div className="page__container">
        <div className="affiliate__create-wrap">
        <div className="affiliate__create">
          <div className="affiliate__input-wrap">
            <input
              className="affiliate__input affiliate__input--code"
              type="text"
              placeholder={t('affiliate.codePlaceholder', 'Промокод')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={64}
            />
            <button
              className="affiliate__input-gen"
              type="button"
              title={t('affiliate.generate', 'Згенерувати')}
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              ⟳
            </button>
          </div>
          <input
            className="affiliate__input"
            type="text"
            placeholder={t('affiliate.namePlaceholder', 'Назва акції')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
          />
          {promoType === 'PARTNER' && (
            <>
              <input
                className="affiliate__input"
                type="text"
                inputMode="numeric"
                placeholder={t('affiliate.creatorPlaceholder', 'ID користувача')}
                value={creatorId}
                onChange={(e) => setCreatorId(e.target.value)}
                maxLength={32}
              />
              <input
                className="affiliate__input"
                type="text"
                placeholder={t('affiliate.creatorNamePlaceholder', 'Юзернейм креатора')}
                value={creatorName}
                onChange={(e) => setCreatorName(e.target.value)}
                maxLength={64}
              />
            </>
          )}
          <button className="affiliate__create-btn" type="button" onClick={handleCreate} disabled={!canCreate || isCreating}>
            {t('affiliate.create', 'Створити промокод')}
          </button>
        </div>

        <div className="affiliate__types">
          {PROMO_TYPES.map((type) => (
            <button
              key={type.value}
              type="button"
              className={`affiliate__type${promoType === type.value ? ' affiliate__type--active' : ''}`}
              onClick={() => setPromoType(type.value)}
            >
              <span className="affiliate__type-label">{t(type.labelKey, type.fallback)}</span>
              <span className="affiliate__type-hint">{t(type.hintKey, type.hintFallback)}</span>
            </button>
          ))}
        </div>

        <div className="affiliate__create affiliate__create--params">
          {(promoType === 'FIXED' || promoType === 'PERCENT' || promoType === 'BALANCE_BONUS' || promoType === 'MULTI_SERVER') && (
            <input
              className="affiliate__input"
              type="text"
              inputMode="decimal"
              placeholder={
                promoType === 'PERCENT' || promoType === 'MULTI_SERVER'
                  ? t('affiliate.percentPlaceholder', 'Відсоток (%)')
                  : t('affiliate.amountPlaceholder', 'Сума ($)')
              }
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
            />
          )}
          {promoType === 'FREE_PERIOD' && (
            <>
              <input
                className="affiliate__input"
                type="text"
                inputMode="numeric"
                placeholder={t('affiliate.freeDaysPlaceholder', 'Днів безкоштовно')}
                value={freeDays}
                onChange={(e) => setFreeDays(e.target.value)}
              />
              <div className="affiliate__plan-drop" ref={planDropRef}>
                <button
                  type="button"
                  className="affiliate__input affiliate__plan-drop-btn"
                  onClick={() => setPlanOpen((v) => !v)}
                >
                  <span className={planId ? '' : 'affiliate__plan-drop-placeholder'}>
                    {planId ? plans.find((p) => String(p.id) === String(planId))?.name || '' : t('affiliate.planAny', 'Будь-який план')}
                  </span>
                  <svg className={`affiliate__plan-drop-chevron${planOpen ? ' affiliate__plan-drop-chevron--open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                </button>
                {planOpen && (
                  <div className="affiliate__plan-drop-list">
                    <button
                      type="button"
                      className={`affiliate__plan-drop-item${!planId ? ' affiliate__plan-drop-item--active' : ''}`}
                      onClick={() => { setPlanId(''); setPlanOpen(false); }}
                    >{t('affiliate.planAny', 'Будь-який план')}</button>
                    {plans.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={`affiliate__plan-drop-item${String(planId) === String(p.id) ? ' affiliate__plan-drop-item--active' : ''}`}
                        onClick={() => { setPlanId(String(p.id)); setPlanOpen(false); }}
                      >{p.name}</button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          {promoType === 'PARTNER' && (
            <>
              <div className="affiliate__types affiliate__types--inline">
                {[
                  { value: 'FIXED', labelKey: 'affiliate.typeFixed', fallback: 'Фікса' },
                  { value: 'PERCENT', labelKey: 'affiliate.typePercent', fallback: 'Відсоток' },
                  { value: 'FREE_PERIOD', labelKey: 'affiliate.typeFreePeriod', fallback: 'Безкоштовно' },
                  { value: 'BALANCE_BONUS', labelKey: 'affiliate.typeBalanceBonus', fallback: 'Бонус' },
                ].map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    className={`affiliate__type${clientDiscountType === type.value ? ' affiliate__type--active' : ''}`}
                    onClick={() => setClientDiscountType(type.value)}
                  >
                    <span className="affiliate__type-label">{t(type.labelKey, type.fallback)}</span>
                  </button>
                ))}
              </div>
              {clientDiscountType === 'FREE_PERIOD' ? (
                <input
                  className="affiliate__input"
                  type="text"
                  inputMode="numeric"
                  placeholder={t('affiliate.freeDaysPlaceholder', 'Днів безкоштовно')}
                  value={freeDays}
                  onChange={(e) => setFreeDays(e.target.value)}
                />
              ) : (
                <input
                  className="affiliate__input"
                  type="text"
                  inputMode="decimal"
                  placeholder={clientDiscountType === 'PERCENT'
                    ? t('affiliate.percentPlaceholder', 'Відсоток (%)')
                    : clientDiscountType === 'BALANCE_BONUS'
                      ? t('affiliate.amountPlaceholder', 'Сума ($)')
                      : t('affiliate.clientDiscountPlaceholder', 'Знижка клієнту ($)')}
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                />
              )}
              <input
                className="affiliate__input"
                type="text"
                inputMode="decimal"
                placeholder={t('affiliate.rewardPlaceholder', 'Винагорода партнеру ($)')}
                value={partnerReward}
                onChange={(e) => setPartnerReward(e.target.value)}
              />
            </>
          )}
          {promoType === 'MULTI_SERVER' && (
            <input
              className="affiliate__input"
              type="text"
              inputMode="numeric"
              placeholder={t('affiliate.minServersPlaceholder', 'Мін. серверів')}
              value={minServers}
              onChange={(e) => setMinServers(e.target.value)}
            />
          )}
          <input
            className="affiliate__input"
            type="text"
            inputMode="numeric"
            placeholder={t('affiliate.maxUsesPlaceholder', 'Макс. активацій')}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
          />
          <input
            className="affiliate__input"
            type="text"
            inputMode="numeric"
            placeholder={t('affiliate.validDaysPlaceholder', 'Днів дії')}
            value={validDays}
            onChange={(e) => setValidDays(e.target.value)}
          />
        </div>
        </div>

        <div className="affiliate__stats">
          <div className="affiliate__stat">
            <span className="affiliate__stat-value">{promos.length}</span>
            <span className="affiliate__stat-label">{t('affiliate.codes', 'Промокодів')}</span>
          </div>
          <div className="affiliate__stat">
            <span className="affiliate__stat-value">{stats.partners}</span>
            <span className="affiliate__stat-label">{t('affiliate.partners', 'Партнерів')}</span>
          </div>
          <div className="affiliate__stat">
            <span className="affiliate__stat-value">{stats.activations}</span>
            <span className="affiliate__stat-label">{t('affiliate.activations', 'Активацій')}</span>
          </div>
          <div className="affiliate__stat">
            <span className="affiliate__stat-value">${stats.paidWithCodes.toFixed(2)}</span>
            <span className="affiliate__stat-label">{t('affiliate.paidWithCodes', 'Сплачено з промокодів')}</span>
          </div>
          <div className="affiliate__stat">
            <span className="affiliate__stat-value">${stats.paidToPartners.toFixed(2)}</span>
            <span className="affiliate__stat-label">{t('affiliate.paidToPartners', 'Виплачено партнерам')}</span>
          </div>
          <div className="affiliate__stat">
            <span className="affiliate__stat-value">${(stats.paidWithCodes - stats.paidToPartners).toFixed(2)}</span>
            <span className="affiliate__stat-label">{t('affiliate.earned', 'Зароблено')}</span>
          </div>
        </div>

        <div className="affiliate__filters">
            <div className="affiliate__filters-inner" ref={filtersInnerRef}>
              <div
                className="affiliate__filters-pill"
                style={{
                  transform: `translateX(${filterPill.left}px)`,
                  width: `${filterPill.width}px`,
                  opacity: filterPill.visible ? 1 : 0,
                }}
              />
              {[
                { value: 'all', label: t('common.all', 'Усі') },
                { value: 'partner', label: t('affiliate.filterPartner', 'Партнерські') },
                { value: 'regular', label: t('affiliate.filterRegular', 'Звичайні') },
              ].map((option) => (
                <button
                  key={option.value}
                  ref={(el) => (filterRefs.current[option.value] = el)}
                  type="button"
                  className={promoFilter === option.value ? 'affiliate__filter affiliate__filter--active' : 'affiliate__filter'}
                  onClick={() => setPromoFilter(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          {filteredPromos.length === 0 && (
            <div className="affiliate__empty">{t('affiliate.noPromos', 'Промокодів поки немає')}</div>
          )}
          <div className="affiliate__grid">
            {filteredPromos.map((promo) => {
              const isInactive = promo.enabled === false || (promo.maxUses && Number(promo.referrals) >= Number(promo.maxUses));
              return (
              <div key={promo.id} className={`affiliate__card ${isInactive ? 'affiliate__card--inactive' : ''}`}>
                <div className="affiliate__card-head">
                  <button className="affiliate__card-code" type="button" onClick={() => handleCopy(promo)}>
                    <span className="affiliate__card-name-label">{t('affiliate.promoCode', 'Промокод')}</span>
                    <span className="affiliate__card-code-value">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      {copiedId === promo.id ? t('affiliate.copied', 'Скопійовано!') : promo.code}
                    </span>
                  </button>
                  {promo.name && (
                    <span className="affiliate__card-name">
                      <span className="affiliate__card-name-label">{t('affiliate.promoName', 'Акція')}</span>
                      {promo.name}
                    </span>
                  )}
                  <span className="affiliate__card-name">
                    <span className="affiliate__card-name-label">{t('affiliate.type', 'Тип')}</span>
                    {(() => {
                      const typeMeta = PROMO_TYPES.find((type) => type.value === promo.type);
                      return typeMeta ? t(typeMeta.labelKey, typeMeta.fallback) : promo.type;
                    })()}
                  </span>
                  {isInactive && (
                    <span className="affiliate__card-name affiliate__card-name--inactive">
                      <span className="affiliate__card-name-label">{t('affiliate.status', 'Статус')}</span>
                      {t('affiliate.inactive', 'Неактивний')}
                    </span>
                  )}
                </div>
                {(promo.creatorName || promo.creatorId) && (
                  <div className="affiliate__card-head">
                    {promo.creatorName && (
                      <span className="affiliate__card-name">
                        <span className="affiliate__card-name-label">{t('affiliate.creator', 'Креатор')}</span>
                        {promo.creatorName}
                      </span>
                    )}
                    {promo.creatorId && (
                      <span className="affiliate__card-name">
                        <span className="affiliate__card-name-label">{t('affiliate.creatorId', 'ID креатора')}</span>
                        {promo.creatorId}
                      </span>
                    )}
                  </div>
                )}
                <div className="affiliate__card-head">
                  <span className="affiliate__card-name">
                    <span className="affiliate__card-name-label">{t('affiliate.maxUsesLabel', 'Активацій')}</span>
                    {promo.maxUses || t('affiliate.unlimited', 'Безліміт')}
                  </span>
                  <span className="affiliate__card-name">
                    <span className="affiliate__card-name-label">{t('affiliate.validDaysLabel', 'Діє')}</span>
                    {promo.validDays ? `${promo.validDays} ${t('affiliate.days', 'дн.')}` : t('affiliate.noExpiry', 'Безстроково')}
                  </span>
                  <span className="affiliate__card-name">
                    <span className="affiliate__card-name-label">
                      {(promo.type === 'BALANCE_BONUS' || (promo.type === 'PARTNER' && promo.clientDiscountType === 'BALANCE_BONUS'))
                        ? t('affiliate.onTopup', 'При поповненні')
                        : (promo.type === 'FREE_PERIOD' || (promo.type === 'PARTNER' && promo.clientDiscountType === 'FREE_PERIOD'))
                          ? t('affiliate.freePeriodLabel', 'Безкоштовний період')
                          : t('affiliate.discountLabel', 'Знижка')}
                    </span>
                    {(() => {
                      const v = promo.discountValue;
                      if (promo.type === 'FREE_PERIOD') return promo.freeDays ? `${promo.freeDays} ${t('affiliate.days', 'дн.')}` : '—';
                      if (promo.type === 'PARTNER') {
                        if (promo.clientDiscountType === 'FREE_PERIOD') return promo.freeDays ? `${promo.freeDays} ${t('affiliate.days', 'дн.')}` : '—';
                        if (promo.clientDiscountType === 'PERCENT' && v) return `-${v}%`;
                        if (promo.clientDiscountType === 'BALANCE_BONUS' && v) return `+$${v}`;
                        return v ? `-$${v}` : '—';
                      }
                      if (!v) return '—';
                      if (promo.type === 'PERCENT' || promo.type === 'MULTI_SERVER') return `-${v}%`;
                      if (promo.type === 'BALANCE_BONUS') return `+$${v}`;
                      return `-$${v}`;
                    })()}
                  </span>
                  {promo.type === 'MULTI_SERVER' && promo.minServers && (
                    <span className="affiliate__card-name">
                      <span className="affiliate__card-name-label">{t('affiliate.minServersLabel', 'Від серверів')}</span>
                      {promo.minServers}
                    </span>
                  )}
                </div>
                {promo.type === 'PARTNER' && (
                  <div className="affiliate__card-desc">
                    <span className="affiliate__card-desc-row">
                      <span className="affiliate__card-desc-label">{t('affiliate.client', 'Клієнт')}</span>
                      {
                        promo.clientDiscountType === 'FREE_PERIOD' && promo.freeDays
                          ? `${promo.freeDays} ${t('affiliate.days', 'дн.')} ${t('affiliate.freeOfCharge', 'безкоштовно')}`
                          : promo.clientDiscountType === 'PERCENT' && promo.discountValue
                            ? `-${promo.discountValue}%`
                            : promo.clientDiscountType === 'BALANCE_BONUS' && promo.discountValue
                              ? `+$${promo.discountValue} ${t('affiliate.typeBalanceBonus', 'Бонус').toLowerCase()}`
                              : promo.discountValue ? `-$${promo.discountValue}` : '—'
                      }
                    </span>
                    <span className="affiliate__card-desc-row">
                      <span className="affiliate__card-desc-label">{t('affiliate.partner', 'Партнер')}</span>
                      {promo.partnerReward ? `$${promo.partnerReward} ${t('affiliate.perActivation', 'за активацію')}` : '—'}
                    </span>
                  </div>
                )}
                <div className="affiliate__card-stats">
                  <div className="affiliate__card-stat">
                    <span className="affiliate__card-stat-label">{t('affiliate.referrals', 'Використань')}</span>
                    <span className="affiliate__card-stat-value">{promo.referrals}</span>
                  </div>
                  {promo.type === 'PARTNER' && (
                    <div className="affiliate__card-stat">
                      <span className="affiliate__card-stat-label">{t('affiliate.partnerEarned', 'Заробив партнер')}</span>
                      <span className="affiliate__card-stat-value">${promo.partnerEarned.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="affiliate__card-stat">
                    <span className="affiliate__card-stat-label">{t('affiliate.ownEarned', 'Я заробив')}</span>
                    <span className="affiliate__card-stat-value">${promo.ownEarned.toFixed(2)}</span>
                  </div>
                </div>
                <div className="affiliate__card-actions">
                  {promo.type === 'PARTNER' && (
                    <button className="affiliate__card-action" type="button" onClick={() => handleRegenerate(promo)}>
                      {t('affiliate.regenerate', 'Новий промокод')}
                    </button>
                  )}
                  {promo.type === 'PARTNER' ? (
                    <button className="affiliate__card-action" type="button" onClick={() => handleRevoke(promo)}>
                      {t('affiliate.revoke', 'Забрати партнерку')}
                    </button>
                  ) : (
                    <button className="affiliate__card-action" type="button" onClick={() => handleDelete(promo)}>
                      {t('affiliate.delete', 'Видалити промокод')}
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
      </div>
    </div>
  );
}

export default AffiliatePage;
